import { useContext, useState } from 'react'
import { SearchableSelectOption } from '../BaseComponents/Forms/SearchableSelect'
import DarkSearchableSelect from '../BaseComponents/Forms/DarkSearchableSelect'
import { userContext } from '../../context/UserContext'
import useApi from '../../hooks/useApi'
import { getApiErrorMessage, getCSRFToken } from '../../lib/utils'

export const FICTITIOUS_HINTS: Record<string, string> = {
	training: 'Training',
	demo: 'Demo',
	sample: 'Sample',
}

export const SUFFIX_CLASS = 'text-charcoal-300'

type OrganisationOption = SearchableSelectOption<string> & { companyId: string; name: string; suffix?: string; isDisabled?: boolean }

// B-7: replaces the sidebar's plain organisation-name heading when the user has 2+ visible
// memberships and no assumption is active - this is membership, never assumption, so it never
// reads or writes the assume header/state.
function OrganisationSwitcher() {
	const { userData } = useContext(userContext)
	const api = useApi()
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')

	const companyName = userData?.original?.companyName || 'No organisation assigned'
	const memberships = userData?.memberships ?? []
	const currentCompanyId = userData?.original?.companyId
	const isAssumed = !!userData?.access?.assumedCompanyType

	// Fictitious organisations are hidden when the preference is off, but the current
	// organisation is always listed so a user never loses sight of where they are.
	const visibleMemberships = memberships.filter(
		(membership) =>
			!membership.fictitiousKind ||
			userData?.showFictitiousOrganisations !== false ||
			membership.companyId === currentCompanyId
	)

	if (isAssumed || visibleMemberships.length < 2) {
		// 9px = the select's 1px border + 8px padding, so the name lines up with the dropdown's text.
		const currentKind = memberships.find((membership) => membership.companyId === currentCompanyId)?.fictitiousKind
		return (
			<span className='px-[9px]'>
				{companyName}
				{currentKind && <span className={SUFFIX_CLASS}> ({FICTITIOUS_HINTS[currentKind]})</span>}
			</span>
		)
	}

	const options: OrganisationOption[] = visibleMemberships.map((membership) => {
		const suffix = membership.fictitiousKind ? `(${FICTITIOUS_HINTS[membership.fictitiousKind]})` : undefined
		return {
			value: membership.membershipId,
			companyId: membership.companyId,
			name: membership.companyName,
			suffix,
			label: suffix ? `${membership.companyName} ${suffix}` : membership.companyName,
			isDisabled: membership.companyLocked,
		}
	})
	const currentOption = options.find((option) => option.companyId === currentCompanyId)

	const onChange = async (option: OrganisationOption | null) => {
		if (!option || option.value === currentOption?.value) {
			return
		}
		setBusy(true)
		setError('')
		try {
			await api.post('organisations/select', {
				membershipId: option.value,
				_csrf: await getCSRFToken(),
			})
			window.location.assign('/')
		} catch (err) {
			setError(getApiErrorMessage(err, 'Failed to switch organisation. Please try again.'))
			setBusy(false)
		}
	}

	return (
		<div>
			<DarkSearchableSelect
				aria-label='Switch organisation'
				isSearchable={false}
				isClearable={false}
				isDisabled={busy}
				options={options}
				value={currentOption}
				onChange={onChange}
				formatOptionLabel={(option: OrganisationOption) => (
					<>
						{option.name}
						{option.suffix && <span className={SUFFIX_CLASS}> {option.suffix}</span>}
					</>
				)}
			/>
			{error && <div className='text-xs font-normal text-error-400 mt-1'>{error}</div>}
		</div>
	)
}

export default OrganisationSwitcher
